// Files in and out: the page's own downloads, and a working folder on disk through the File
// System Access API where the browser has it (Chrome and Edge; decision 14). The folder's handle
// is kept in IndexedDB, so the next visit can ask for it again with one click.

export const DOCUMENT = /\.(docx|md|markdown|txt)$/i
export const SESSION_SUFFIX = '.align.json'

/// A file's name without its folder and its extension, `.align.json` counting as one.
export function stem(name: string): string {
  const base = name.split(/[\\/]/).pop() ?? name
  if (base.toLowerCase().endsWith(SESSION_SUFFIX)) return base.slice(0, -SESSION_SUFFIX.length)
  return base.replace(/\.[^.]+$/, '') || base
}

/// Chapters in name order, with numbers compared as numbers, so chapter 2 comes before 10.
export const byName = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }).compare

export function download(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }))
  const a = document.createElement('a')
  a.href = url
  a.download = name
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

type Picker = (options?: { id?: string; mode?: 'read' | 'readwrite' }) => Promise<FileSystemDirectoryHandle>
type Permission = { queryPermission(o: { mode: string }): Promise<PermissionState>; requestPermission(o: { mode: string }): Promise<PermissionState> }

export const folderSupported = typeof (window as unknown as { showDirectoryPicker?: Picker }).showDirectoryPicker === 'function'

export async function pickFolder(): Promise<FileSystemDirectoryHandle> {
  const folder = await (window as unknown as { showDirectoryPicker: Picker }).showDirectoryPicker({ id: 'interplae-align', mode: 'readwrite' })
  await remember(folder)
  return folder
}

/// Whether the page may still read and write the folder; asking needs a click.
export async function folderAllowed(folder: FileSystemDirectoryHandle, ask: boolean): Promise<boolean> {
  const handle = folder as unknown as Permission
  const mode = { mode: 'readwrite' }
  if ((await handle.queryPermission(mode)) === 'granted') return true
  return ask && (await handle.requestPermission(mode)) === 'granted'
}

export async function folderFiles(folder: FileSystemDirectoryHandle): Promise<string[]> {
  const names: string[] = []
  for await (const [name, entry] of (folder as unknown as { entries(): AsyncIterable<[string, FileSystemHandle]> }).entries()) {
    if (entry.kind === 'file' && !name.startsWith('.')) names.push(name)
  }
  return names.sort(byName)
}

export async function readFromFolder(folder: FileSystemDirectoryHandle, name: string): Promise<File> {
  return (await folder.getFileHandle(name)).getFile()
}

export async function writeToFolder(folder: FileSystemDirectoryHandle, name: string, text: string) {
  const handle = await folder.getFileHandle(name, { create: true })
  const writable = await (handle as unknown as { createWritable(): Promise<FileSystemWritableFileStream> }).createWritable()
  await writable.write(text)
  await writable.close()
}

const DB = 'interplae-align'

function store<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open(DB, 1)
    open.onupgradeneeded = () => open.result.createObjectStore('handles')
    open.onerror = () => reject(open.error)
    open.onsuccess = () => {
      const request = run(open.result.transaction('handles', mode).objectStore('handles'))
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    }
  })
}

async function remember(folder: FileSystemDirectoryHandle) {
  await store('readwrite', s => s.put(folder, 'folder')).catch(() => {})
}

export async function rememberedFolder(): Promise<FileSystemDirectoryHandle | null> {
  if (!folderSupported) return null
  return (await store<FileSystemDirectoryHandle | undefined>('readonly', s => s.get('folder')).catch(() => undefined)) ?? null
}

export async function forgetFolder() {
  await store('readwrite', s => s.delete('folder')).catch(() => {})
}
